import { Dialog } from "@headlessui/react";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/Button.jsx";
import { Input } from "../components/ui/Input.jsx";
import { Label } from "../components/ui/Label.jsx";
import { useToast } from "../hooks/useToast.js";

export function EditCompanyModal({
  isOpen,
  setIsOpen,
  company,
  onCompanyUpdated,
}) {
  const [formData, setFormData] = useState({
    company_name: "",
    company_email: "",
    company_phone: "",
    company_address: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    if (company) {
      setFormData({
        company_name: company.company_name || "",
        company_email: company.company_email || "",
        company_phone: company.company_phone || "",
        company_address: company.company_address || "",
      });
      setErrors({});
    }
  }, [company]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const trimmedValue = value.trimStart();
    setFormData((prev) => ({ ...prev, [name]: trimmedValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    const name = formData.company_name.trim();
    const email = formData.company_email.trim();
    const phone = formData.company_phone.trim();
    const address = formData.company_address.trim();

    if (!name) newErrors.company_name = "Company name is required.";
    if (!email) newErrors.company_email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.company_email = "Email must include '@' and '.' in a valid format.";
    if (!phone) newErrors.company_phone = "Phone number is required.";
    else if (!/^[0-9+\-\s]{10,}$/.test(phone))
      newErrors.company_phone = "Invalid phone number format.";
    if (!address) newErrors.company_address = "Address is required.";

    setErrors(newErrors);
    return newErrors;
  };

  const handleSave = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      const errorMessages = Object.values(validationErrors).join(", ");
      toast({
        title: "Error",
        description: errorMessages,
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      await onCompanyUpdated(formData);
      toast({
        title: "Success",
        description: `Company details have been updated.`,
      });
      setIsOpen(false);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to update company. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog
          static
          open={isOpen}
          onClose={() => setIsOpen(false)}
          className="fixed inset-0 z-50 flex flex-col justify-end"
        >
          {/* Background Overlay */}
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setIsOpen(false)}
          />

          {/* Bottom Sheet Modal */}
          <motion.div
            key="company-edit-modal"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{
              type: "spring",
              stiffness: 180,
              damping: 22,
              mass: 0.5,
            }}
            className="relative w-full bg-white rounded-t-3xl shadow-lg p-5 z-50"
          >
            {/* Handle bar */}
            <div
              className="bg-gray-400/70 absolute top-1 left-1/2 -translate-x-1/2 h-1 w-16 rounded-full"
              onClick={() => setIsOpen(false)}
            ></div>

            <Dialog.Title className="text-base font-semibold text-gray-800 mb-4 text-center">
              Edit Company Details
            </Dialog.Title>

            <div className="space-y-3 mb-4">
              {/* Company Name */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Company Name
                </Label>
                <Input
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="Company Name"
                  className="h-8 text-xs border-blue-100/50"
                />
                {errors.company_name && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.company_name}
                  </p>
                )}
              </div>

              {/* Company Email */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Company Email
                </Label>
                <Input
                  name="company_email"
                  type="email"
                  value={formData.company_email}
                  onChange={handleChange}
                  placeholder="Company Email"
                  className="h-8 text-xs border-blue-100/50"
                />
                {errors.company_email && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.company_email}
                  </p>
                )}
              </div>

              {/* Company Phone */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Company Phone
                </Label>
                <Input
                  name="company_phone"
                  value={formData.company_phone}
                  onChange={handleChange}
                  maxLength={10}
                  pattern="\d{10}"
                  placeholder="Company Phone"
                  className="h-8 text-xs border-blue-100/50"
                />
                {errors.company_phone && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.company_phone}
                  </p>
                )}
              </div>

              {/* Company Address */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Company Address
                </Label>
                <Input
                  name="company_address"
                  value={formData.company_address}
                  onChange={handleChange}
                  placeholder="Company Address"
                  className="h-8 text-xs border-blue-100/50"
                />
                {errors.company_address && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.company_address}
                  </p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                className="h-8 px-3 text-xs rounded-full border border-gray-200 text-gray-600"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading}
                className="h-8 px-4 text-xs rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391] text-white border "
              >
                {loading ? "Updating..." : "Update"}
              </Button>
            </div>
          </motion.div>
        </Dialog>
      )}
    </AnimatePresence>
  );
}
